import React from 'react';
import { CartesianGrid, Legend, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';
import { fmtDate } from '../../lib/dates';
import { OUT_LABEL, VAL_LABEL } from '../../lib/evaluation';

const LEVELS = ['', 'Muy desfav.', 'Desfav.', 'Incierta', 'Favorable', 'Muy fav.'];
const COLORS = { sentencia_favorable: '#f59e0b', admisibilidad: '#38bdf8', cautelar: '#a78bfa', recurso: '#34d399', otro: '#94a3b8' };

const Tip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const e = payload[0].payload.e;
  return <div className="max-w-xs rounded-lg border border-slate-700 bg-slate-900 p-2 text-xs text-slate-200"><p className="font-semibold">{VAL_LABEL[e.valoracion]} · {OUT_LABEL[e.resultado]}</p><p>{fmtDate(e.created_at, true)} · incertidumbre {e.incertidumbre}</p><p className="text-slate-400">{e.metodo}</p>{e.eventos?.map((x, i) => <p key={i} className="text-amber-300">◆ {x.descripcion} ({x.fecha})</p>)}</div>;
};

// One series per evaluated outcome (never mixed); legacy AI percentages are listed apart as non-comparable
export const EvaluationChart = ({ data }) => {
  const ev = data.evaluaciones;
  const series = Object.keys(OUT_LABEL).map((k) => [k, ev.filter((e) => e.resultado === k).map((e) => ({ x: new Date(e.created_at).getTime(), y: e.nivel, e }))]).filter(([, p]) => p.length);
  return (
    <div data-testid="evaluation-history" className="flex flex-col gap-3">
      {ev.length ? (
        <div className="h-56 w-full">
          <ResponsiveContainer>
            <ScatterChart margin={{ left: 10, right: 20, top: 10, bottom: 10 }}>
              <CartesianGrid stroke="#1e293b" />
              <XAxis dataKey="x" type="number" domain={[(min) => min - 86400000, (max) => max + 86400000]} scale="time" tickCount={4} dy={6} tickFormatter={(t) => new Date(t).toLocaleDateString('es-CL')} stroke="#64748b" fontSize={11} />
              <YAxis dataKey="y" type="number" domain={[0.5, 5.5]} ticks={[1, 2, 3, 4, 5]} tickFormatter={(v) => LEVELS[v]} stroke="#64748b" fontSize={11} width={90} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Tooltip content={<Tip />} />
              {series.map(([k, pts]) => <Scatter key={k} name={OUT_LABEL[k]} data={pts} fill={COLORS[k]} line={{ stroke: COLORS[k] }} shape={(p) => (p.payload.e.eventos?.length ? <path d={`M${p.cx},${p.cy - 7}L${p.cx + 7},${p.cy}L${p.cx},${p.cy + 7}L${p.cx - 7},${p.cy}Z`} fill={COLORS[k]} /> : <circle cx={p.cx} cy={p.cy} r={5} fill={COLORS[k]} />)} />)}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      ) : <p className="text-sm text-slate-500">Aún no hay valoraciones registradas. No se inventan puntos históricos.</p>}
      <div className="flex flex-col gap-1">
        {[...ev].reverse().map((e) => (
          <div key={e.id} data-testid={`evaluation-${e.id}`} className="rounded-lg border border-slate-800 p-2 text-xs">
            <p><b>{VAL_LABEL[e.valoracion]}</b> · {OUT_LABEL[e.resultado]} · {fmtDate(e.created_at, true)} · incertidumbre {e.incertidumbre} · {e.metodo}{e.matriz_version ? ` · matriz v${e.matriz_version}` : ''} · {e.autor}</p>
            <p className="text-slate-400">{e.fundamentos}</p>
            {e.eventos.map((x, i) => <p key={i} className="text-amber-300">◆ {data.eventos[x.tipo]}: {x.descripcion} ({x.fecha})</p>)}
          </div>
        ))}
      </div>
      {!!data.legado.length && (
        <details data-testid="evaluation-legacy" className="text-xs text-slate-500"><summary className="cursor-pointer">Porcentajes antiguos ({data.legado.length}) · IA sin calibrar, no comparables con las valoraciones</summary>
          {data.legado.map((l) => <p key={l.at}>{fmtDate(l.at, true)} · {Math.round(l.p * 100)}% · {l.metodo}</p>)}
        </details>
      )}
    </div>
  );
};
